/**
 * ReportHistorySelector Component
 *
 * Lists the authenticated user's FundElite imports ordered by import timestamp.
 * Labels use imported_at (FundElite), not provider report_date.
 */

import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Calendar, Check, AlertCircle, History } from 'lucide-react';
import { useReportData } from '../../context/ReportDataContext';
import { useTheme } from '../../layouts/SelfServePortalLayout';
import { useAuth } from '../../context/AuthContext';
import { useSupportContext } from '../../context/SupportContext';
import { useI18n } from '../../context/I18nContext';
import { filterReportHistoryRows } from '../../utils/reportHistoryRows';

const getImportDisplayDate = (report) =>
  report?.display_import_date || report?.display_date || '';

const ReportHistorySelector = ({ onReportChange, className = '' }) => {
  const { t } = useI18n();
  const CR = 'creditReport';
  const { user } = useAuth();
  const { isInSupportMode, supportContext } = useSupportContext();
  const { selectedReportId, reportData, selectReport, isReportLocked, availableReports, fetchAvailableReports } = useReportData();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const [isOpen, setIsOpen] = useState(false);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);

  const theme = {
    selector: {
      bg: isDark ? 'rgba(10,34,25,0.72)' : 'rgba(255,255,255,0.88)',
      border: isDark ? 'rgba(142,218,182,0.14)' : 'rgba(15,172,108,0.12)',
      borderHover: isDark ? 'rgba(142,218,182,0.24)' : 'rgba(15,172,108,0.22)',
      borderFocus: isDark ? 'rgba(15,172,108,0.46)' : 'rgba(15,172,108,0.36)',
      shadow: isDark
        ? '0 4px 16px rgba(0,0,0,0.24)'
        : '0 4px 16px rgba(16,24,40,0.08)',
    },
    text: {
      primary: isDark ? '#EAF7F0' : '#111827',
      secondary: isDark ? '#C7DDD0' : '#4B5563',
      muted: isDark ? '#93AE9F' : '#6B7280',
    },
    accent: {
      emerald: isDark ? '#56B988' : '#0D9F64',
      emeraldBg: isDark ? 'rgba(86,185,136,0.14)' : 'rgba(13,159,100,0.10)',
    },
    badge: {
      latest: {
        bg: isDark ? 'rgba(15,172,108,0.18)' : 'rgba(15,172,108,0.12)',
        color: isDark ? '#8FE0B7' : '#0B8C58',
      },
      historical: {
        bg: isDark ? 'rgba(234,178,58,0.14)' : 'rgba(234,178,58,0.12)',
        color: isDark ? '#F0C160' : '#CA8A04',
      },
    },
    dropdown: {
      bg: isDark ? 'rgba(7,28,21,0.96)' : 'rgba(255,255,255,0.98)',
      border: isDark ? 'rgba(142,218,182,0.12)' : 'rgba(15,172,108,0.10)',
      shadow: isDark
        ? '0 12px 40px rgba(0,0,0,0.42)'
        : '0 12px 40px rgba(16,24,40,0.14)',
      itemHover: isDark ? 'rgba(142,218,182,0.08)' : 'rgba(15,172,108,0.06)',
      itemSelected: isDark ? 'rgba(15,172,108,0.14)' : 'rgba(15,172,108,0.10)',
    },
  };

  useEffect(() => {
    if (availableReports.length > 0) {
      setReports(availableReports);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        await fetchAvailableReports();
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch report history:', err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [availableReports.length, fetchAvailableReports]);

  useEffect(() => {
    if (availableReports.length > 0) {
      const effectiveUserId =
        (isInSupportMode ? supportContext?.effective_user_id : user?.user_id) || null;
      setReports(filterReportHistoryRows(availableReports, effectiveUserId));
    }
  }, [availableReports, isInSupportMode, supportContext?.effective_user_id, user?.user_id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectReport = async (reportId) => {
    if (reportId === selectedReportId) {
      setIsOpen(false);
      return;
    }

    if (isReportLocked) {
      setIsOpen(false);
      return;
    }

    try {
      await selectReport(reportId);
      onReportChange?.(reportId);
    } catch (err) {
      console.error('Failed to switch report:', err);
    }

    setIsOpen(false);
  };

  const activeReportId = selectedReportId || reportData?.report_id;
  const selectedReport =
    reports.find((r) => r.report_id === activeReportId)
    || reports[0];
  const isViewingHistorical = selectedReport && !selectedReport.is_latest;

  const formatPrimaryLabel = (report) => {
    if (!report) return t(`${CR}.noReportsImported`);
    return t(`${CR}.reportHistoryImportEntry`, {
      importDate: getImportDisplayDate(report),
      provider: report.provider || t(`${CR}.title`),
    });
  };

  const renderDropdownItemDetails = (report) => {
    if (!report) return null;
    const importDate = getImportDisplayDate(report);
    return (
      <div className="flex flex-col gap-0.5 mt-1">
        <span className="text-xs" style={{ color: theme.text.muted }}>
          {t(`${CR}.reportHistoryImportedLabel`, { date: importDate })}
        </span>
        {report.display_report_date ? (
          <span className="text-xs" style={{ color: theme.text.muted }}>
            {t(`${CR}.reportHistoryProviderReportDate`, { date: report.display_report_date })}
          </span>
        ) : null}
        <span className="text-xs" style={{ color: theme.text.muted }}>
          {t(`${CR}.reportHistoryProviderLabel`, { provider: report.provider || t(`${CR}.title`) })}
        </span>
      </div>
    );
  };

  if (!loading && reports.length === 0) {
    return (
      <div
        className={`flex items-center gap-3 px-4 py-3 rounded-xl ${className}`}
        style={{
          background: theme.selector.bg,
          border: `1px solid ${theme.selector.border}`,
          opacity: 0.7,
        }}
        data-testid="report-history-empty"
      >
        <Calendar size={18} style={{ color: theme.text.muted }} />
        <span className="text-sm font-medium" style={{ color: theme.text.muted }}>
          {t(`${CR}.noReportsImported`)}
        </span>
      </div>
    );
  }

  return (
    <div
      ref={dropdownRef}
      className={`relative ${className}`}
      data-testid="report-history-selector"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading || isReportLocked}
        className="flex items-center justify-between gap-3 w-full px-4 py-3 rounded-xl transition-all duration-200"
        style={{
          minWidth: '280px',
          maxWidth: '360px',
          height: '48px',
          background: theme.selector.bg,
          border: `1px solid ${isOpen ? theme.selector.borderFocus : theme.selector.border}`,
          boxShadow: theme.selector.shadow,
          backdropFilter: 'blur(12px)',
          cursor: isReportLocked ? 'not-allowed' : 'pointer',
          opacity: isReportLocked ? 0.7 : 1,
        }}
        data-testid="report-history-trigger"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <History
            size={18}
            style={{ color: theme.accent.emerald, flexShrink: 0 }}
          />
          <span
            className="text-sm font-semibold truncate"
            style={{ color: theme.text.primary }}
          >
            {loading ? t('common.loading') : formatPrimaryLabel(selectedReport)}
          </span>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {selectedReport?.is_latest && (
            <span
              className="px-2 py-0.5 rounded-md text-xs font-bold whitespace-nowrap"
              style={{
                background: theme.badge.latest.bg,
                color: theme.badge.latest.color,
              }}
            >
              {t(`${CR}.latestImportBadge`)}
            </span>
          )}
          <ChevronDown
            size={16}
            className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            style={{ color: theme.text.muted }}
          />
        </div>
      </button>

      {isViewingHistorical && !isOpen && (
        <div
          className="flex items-center gap-2 mt-2 px-3 py-2 rounded-lg text-xs"
          style={{
            background: theme.badge.historical.bg,
            color: theme.badge.historical.color,
          }}
          data-testid="historical-report-banner"
        >
          <AlertCircle size={14} />
          <span className="font-medium">{t(`${CR}.viewingHistoricalReport`)}</span>
          <span className="opacity-80">
            {t(`${CR}.dataFromImportDate`, {
              date: getImportDisplayDate(selectedReport),
            })}
          </span>
        </div>
      )}

      {isOpen && (
        <div
          className="absolute z-50 w-full mt-2 py-2 rounded-xl overflow-hidden max-h-[420px] overflow-y-auto overscroll-contain"
          style={{
            minWidth: '320px',
            maxWidth: '420px',
            background: theme.dropdown.bg,
            border: `1px solid ${theme.dropdown.border}`,
            boxShadow: theme.dropdown.shadow,
            backdropFilter: 'blur(16px)',
          }}
          data-testid="report-history-dropdown"
        >
          <div
            className="px-4 py-2 border-b"
            style={{ borderColor: theme.dropdown.border }}
          >
            <span
              className="text-xs font-bold uppercase tracking-wide"
              style={{ color: theme.text.muted }}
            >
              {t(`${CR}.reportHistory`)}
            </span>
          </div>

          <div className="py-1">
            {reports.map((report) => {
              const isSelected = report.report_id === activeReportId;

              return (
                <button
                  key={report.report_id}
                  onClick={() => handleSelectReport(report.report_id)}
                  className="w-full px-4 py-3 text-left transition-colors"
                  style={{
                    background: isSelected ? theme.dropdown.itemSelected : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.background = theme.dropdown.itemHover;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isSelected
                      ? theme.dropdown.itemSelected
                      : 'transparent';
                  }}
                  data-testid={`report-option-${report.report_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold" style={{ color: theme.text.primary }}>
                          {formatPrimaryLabel(report)}
                        </span>
                        {report.is_latest && (
                          <span
                            className="px-1.5 py-0.5 rounded text-[10px] font-bold"
                            style={{
                              background: theme.badge.latest.bg,
                              color: theme.badge.latest.color,
                            }}
                          >
                            {t(`${CR}.latestImportBadge`)}
                          </span>
                        )}
                      </div>

                      {renderDropdownItemDetails(report)}

                      <div
                        className="text-xs mt-1.5 flex items-center gap-1"
                        style={{ color: theme.text.muted }}
                      >
                        <span>
                          {report.bureaus_imported > 0
                            ? (report.bureaus_imported === 1
                              ? t(`${CR}.bureausCount`, { count: report.bureaus_imported })
                              : t(`${CR}.bureausCountPlural`, { count: report.bureaus_imported }))
                            : t(`${CR}.bureausNotAvailable`)}
                        </span>
                        <span>•</span>
                        <span>
                          {report.average_score
                            ? t(`${CR}.avgScore`, { score: report.average_score })
                            : t(`${CR}.avgScoreNotAvailable`)}
                        </span>
                      </div>
                    </div>

                    {isSelected && (
                      <Check
                        size={18}
                        style={{ color: theme.accent.emerald, flexShrink: 0 }}
                      />
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {reports.length === 0 && !loading && (
            <div className="px-4 py-6 text-center">
              <p className="text-sm" style={{ color: theme.text.muted }}>
                Import your credit report to begin.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportHistorySelector;
